import i18n from '../translations/i18n';
import { isValidEnum, ObjectTypes } from '../utils/utils';
import configurationsClient from './configurations-service';
import {
  PlantEntityType,
  PlantFlatEntityDTO,
} from '@/../client-generator/generated-clients/configurations';
import {
  RoomDTO,
  RoomType,
  RoomObjectType,
  AddRoomObjectDTO,
  IRoomObject,
  PlantListDTO,
} from '@/../client-generator/generated-clients/plants';

export interface IValidableObjectType extends AddRoomObjectDTO {
  idx?: number;
  isInvalid?: boolean;
  invalidReason?: string;
}

export interface RoomObjectsTypes {
  id: string;
  objects: Array<IValidableObjectType>;
  type: RoomObjectType;
  busy?: boolean;
}

export type ValidablePlant = Partial<PlantListDTO> & {
  isInvalid?: boolean;
  invalidReason?: string;
};

export interface ISuggestValidObjectNameOpts {
  plantId: number;
  eType: PlantEntityType;

  /**
   * Additional data required in certain scenarios
   * in order to be able to suggest the next valid name
   */
  metadata?: {
    /**
     * In case we are suggesting the name for a
     * room object, we need the Room
     */
    room?: RoomDTO;

    /**
     * In case we are suggesting the name for a
     * room object or a group, we need the RoomObjectType
     */
    roomObjectType?: RoomObjectType;

    /**
     * In case we are suggesting the name for
     * a room, we need the RoomType
     */
    roomType?: RoomType;

    /**
     * In case we are suggesting the name for
     * a cloned plant, we always start with a
     * source name
     */
    plantSourceName?: string;
  };
}

export class NamingService {
  private static $t = i18n.global.t;

  /**
   * Used to cache requests. This turns out to be
   * very useful when subsequent entities of the same
   * type must be validated. Without this, the validation
   * method would call the API for each validation.
   */
  private static initialEntities: PlantFlatEntityDTO[] = [];
  private static additionalEntitiesInternal: PlantFlatEntityDTO[] = [];

  static async prefetchFlatEntities(
    plantId: number,
    eType: PlantEntityType
  ): Promise<boolean> {
    this.initialEntities = await configurationsClient.flattenedEntities(
      plantId,
      eType
    );
    return Promise.resolve(!!this.initialEntities);
  }

  static set additionalEntities(v: PlantFlatEntityDTO[]) {
    this.additionalEntitiesInternal = v;
  }

  static get additionalEntities(): PlantFlatEntityDTO[] {
    return this.additionalEntitiesInternal;
  }

  static flushCache(): void {
    this.initialEntities = [];
    this.additionalEntitiesInternal = [];
  }

  /**
   * Convenience method to update a cached name without
   * flushing and reloading the cache again
   *
   * @param currentName the entity name you want edit
   * @param newName the new entity name
   */
  static forcefullyRenameFirstEntityWithinCachedOnes(
    currentName: string,
    newName: string
  ): void {
    const initialEntitiesWantedIdx = this.initialEntities.findIndex(
      (e) =>
        e.currentName?.trim().toLowerCase() === currentName.trim().toLowerCase()
    );
    if (initialEntitiesWantedIdx !== -1)
      this.initialEntities[initialEntitiesWantedIdx].currentName = newName;

    const additionalEntitiesWantedIdx = this.additionalEntities.findIndex(
      (e) =>
        e.currentName?.trim().toLowerCase() === currentName.trim().toLowerCase()
    );
    if (additionalEntitiesWantedIdx !== -1)
      this.additionalEntities[
        additionalEntitiesWantedIdx
      ].currentName = newName;
  }

  static isValidEntityTypeName(name?: string): boolean {
    if (!name || !name.trim()) return false;
    return (
      this.initialEntities.every(
        (e) =>
          e.currentName?.toLowerCase().trim() !== name?.toLowerCase().trim()
      ) &&
      this.additionalEntities.every(
        (e) =>
          e.currentName?.toLowerCase().trim() !== name?.toLowerCase().trim()
      )
    );
  }

  static async validateRoomObjectAgainstList(
    list: RoomObjectsTypes[],
    objectToValidate: IRoomObject
  ): Promise<RoomObjectsTypes[]> {
    for (const oType of list) {
      let sameNameOccurrences = 0;
      for (const innerObject of oType.objects) {
        if (
          innerObject.name?.toLowerCase().trim() ===
          objectToValidate.name?.toLowerCase().trim()
        ) {
          sameNameOccurrences++;
          innerObject.isInvalid = sameNameOccurrences > 1;
        } else {
          innerObject.isInvalid = false;
        }
      }
    }
    return Promise.resolve(list);
  }

  static validatePlantAgainstList(list: ValidablePlant[]): ValidablePlant[] {
    for (const loopingPlant of list) {
      loopingPlant.isInvalid =
        list.filter(
          (e) =>
            e.name?.toLowerCase().trim() ===
            loopingPlant.name?.toLowerCase().trim()
        ).length > 1;
    }
    return list;
  }

  /**
   * Suggests the next valid name for an entity according to the
   * configuration of the options provided:
   *
   * The minimum required options are the plantId and the PlantEntityType.
   * Other than those, there are specific combinations to use in order to
   * suggest the name for each entity type. These combinations are provided
   * to the "metadata" property of the configuration object:
   *
   * - Scene: no metadata required
   * - Room: metadata.roomType must be valorized
   * - Group: metadata.roomObjectType must be valorized
   * - RoomObject: metadata.room, metadata.roomType,
   *   metadata.roomObjectType must be valorized
   *
   * @param opts
   * @returns Promise<string> the next valid name that can be used
   */
  static async suggestNextValidEntityName(
    opts: ISuggestValidObjectNameOpts
  ): Promise<string> {
    let modifier = 0;
    let baseName: string = this.initEntityNameOrThrow(opts);
    let name = '';

    do {
      modifier++;
      name =
        modifier === 1 ? baseName.trim() : baseName.trim() + ` ${modifier}`;
    } while (!this.isValidEntityTypeName(name));

    return Promise.resolve(name);
  }

  private static initEntityNameOrThrow(
    opts: ISuggestValidObjectNameOpts
  ): string {
    // RoomObject name suggestion
    if (
      opts.eType === PlantEntityType.ROOM_OBJECT &&
      !!opts.metadata?.room &&
      isValidEnum(opts.metadata?.roomType) &&
      isValidEnum(opts.metadata?.roomObjectType)
    ) {
      return this.initRoomObjectName(
        opts.metadata.room,
        opts.metadata?.roomObjectType as number
      );
    }

    // Room name suggestion
    if (
      opts.eType === PlantEntityType.ROOM &&
      isValidEnum(opts.metadata?.roomType)
    ) {
      return this.initRoomName(opts.metadata?.roomType as number);
    }

    // Scene name suggestion
    if (opts.eType === PlantEntityType.SCENE) {
      return this.initSceneName();
    }

    // Group name suggestion
    if (
      opts.eType === PlantEntityType.GROUP &&
      isValidEnum(opts.metadata?.roomObjectType)
    ) {
      return this.initGroupName(opts.metadata?.roomObjectType as number);
    }

    // Plant name suggestion
    if (opts.eType === PlantEntityType.PLANT) {
      if (!opts.metadata?.plantSourceName)
        throw new Error(
          'PlantSourceName is required to suggest the next plant name'
        );
      return this.initPlantName(opts.metadata?.plantSourceName!);
    }

    const errMsg = `[X](${NamingService.name}): Could not infer the entity name to generate. Check your opts combination.`;
    // this log is for convenience since Vue messes up the messages of the errors in console.
    console.error(errMsg);
    throw new Error(errMsg);
  }

  private static initPlantName(sourceName: string): string {
    return `${sourceName.trim()} ${this.$t('AppResource.Copy').toLowerCase()}`;
  }

  private static initGroupName(roomObjectType: RoomObjectType): string {
    switch (roomObjectType) {
      case RoomObjectType.Shutter:
        return this.$t('AppResource.GroupOfShutters');
      case RoomObjectType.Light:
        return this.$t('AppResource.GroupOfLights');
      default:
        return '';
    }
  }

  private static initSceneName(): string {
    return this.$t(`AppResource.Scene`);
  }

  private static initRoomName(roomType: RoomType): string {
    return this.$t(`AppResource.${RoomType[roomType]}`);
  }

  private static initRoomObjectName(
    room: RoomDTO,
    objType: RoomObjectType
  ): string {
    return `${room.name} ` + this.$t(`AppResource.${RoomObjectType[objType]}`);
  }
}
